// Copies of the main app's Hyperscout-Frontend/src/components/Modals/UploadExhibitorsCSVModal/
// formatExhibitorRows.js and parseExhibitorFile.js (keep in sync): the same columns and value
// parsing as the fair's own upload, so the admin backend's copy of upsertFair gets the same rows.

import Papa from 'papaparse'
import * as XLSX from 'xlsx/xlsx.mjs'

// Fields a CSV column can be mapped to. ids match the /fair/upsert-fair exhibitor payload
export const EXHIBITOR_FIELDS = [
  { id: 'name', label: 'Customer Name', required: true },
  { id: 'website', label: 'Website', required: true },
  { id: 'type', label: 'Type', required: true },
  { id: 'email', label: 'Email' },
  { id: 'paid', label: 'Registered' },
  { id: 're_booked', label: 'Rebooked Customer' }
]

const isEmpty = (value) => value === undefined || value === null || String(value).trim() === ''

// Unknown values are passed through as-is so the API reports the row as invalid instead of silently guessing
const toBool = (value) => {
  if (typeof value === 'boolean') return value
  const v = String(value).trim().toLowerCase()
  if (['yes', 'y', 'true', '1', 'x'].includes(v)) return true
  if (['no', 'n', 'false', '0'].includes(v)) return false
  return value
}

const toType = (value) => {
  const v = String(value).trim().toLowerCase()
  if (v.startsWith('brand')) return 'brand'
  if (v.startsWith('retailer')) return 'retailer'
  return String(value).trim()
}

// mapping: { [csvHeader]: fieldId }. Rows with no mapped values are dropped
export const formatExhibitorRows = (data, mapping) =>
  data
    .map((raw) => {
      const row = {}
      Object.entries(mapping).forEach(([header, field]) => {
        const value = raw[header]
        if (isEmpty(value)) return
        if (field === 'paid' || field === 're_booked') row[field] = toBool(value)
        else if (field === 'type') row[field] = toType(value)
        else row[field] = String(value).trim()
      })
      return row
    })
    .filter((row) => Object.keys(row).length > 0)


export const MAX_FILE_SIZE_MB = 12
const EXTENSIONS = ['csv', 'xls', 'xlsx']

const makeColumns = (rawColumns) =>
  rawColumns
    ?.filter((column) => column && !String(column).startsWith('_'))
    ?.map((column) => ({ Header: String(column), accessor: String(column) }))

// Resolves { data: rows[], columns: [{ Header, accessor }] }. Rejects with a user-facing message
export const parseExhibitorFile = (file) =>
  new Promise((resolve, reject) => {
    const extension = file.name.toLowerCase().split('.').pop()
    if (!EXTENSIONS.includes(extension)) {
      return reject(new Error('File type not supported. Use .csv, .xls or .xlsx'))
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return reject(new Error(`File is larger than ${MAX_FILE_SIZE_MB} MB`))
    }

    const done = (rows, fields) =>
      rows?.length
        ? resolve({ data: rows, columns: makeColumns(fields) })
        : reject(new Error('The file has no rows'))

    if (extension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (parsed) => done(parsed?.data, parsed?.meta?.fields),
        error: () => reject(new Error('Could not read the file'))
      })
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(new Uint8Array(event.target.result), { type: 'array' })
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const [headers = [], ...lines] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
        const rows = lines
          .filter((line) => line.some((cell) => cell !== undefined && cell !== ''))
          .map((line) => Object.fromEntries(headers.map((header, i) => [header, line[i]])))
        done(rows, headers)
      } catch {
        reject(new Error('Could not read the file'))
      }
    }
    reader.onerror = () => reject(new Error('Could not read the file'))
    reader.readAsArrayBuffer(file)
  })
